import React, { useCallback, useEffect, useRef, useState } from 'react'
import { HiOutlineEmojiSad, HiArrowUp } from 'react-icons/hi'
import ProductCard from './ProductCard'
import { ProductGridSkeleton } from './Skeleton'
import EmptyState from './EmptyState'

const HAS_OBSERVER = typeof window !== 'undefined' && 'IntersectionObserver' in window
// start loading the next page about 1.5 screens before the shopper reaches the end. The large top margin also
// counts the end as reached once it has been scrolled past (a fast scroll into the tall footer on phones)
const PRELOAD_MARGIN = '20000px 0px 1200px 0px'

const formatCount = (n) => new Intl.NumberFormat('en-IN').format(n)
export const stylesLabel = (n) => `${formatCount(n)} ${n === 1 ? 'style' : 'styles'}`

// Re-rendered only when its own item changes, so appending a page doesn't redraw the cards already shown
const FeedCard = React.memo(({ item, onOpen }) => (
    <ProductCard
        name={item.itemName}
        image={item.listImageUrl || item.imageUrl}
        meta={item.sizes}
        mrp={item.mrp}
        finalPrice={item.finalPrice}
        data-item-id={item.itemId}
        onClick={() => onOpen(item.itemId)}
    />
))

const backToTop = () => {
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' })
}

// Product grid that keeps loading as the shopper scrolls (feed from useItemFeed). The first page shows a skeleton;
// later pages add a skeleton row below the cards, so nothing already on screen moves.
const InfiniteProductGrid = ({ feed, onOpen, empty }) => {
    const { items, total, status, added, loadMore, retry, rememberAnchor } = feed
    const sentinel = useRef(null)
    const grid = useRef(null)
    const nearEnd = useRef(false)
    const [announce, setAnnounce] = useState('')
    // remember which card was opened and where it sat, so Back can put it in the same place
    const open = useCallback((itemId) => {
        rememberAnchor(itemId)
        onOpen(itemId)
    }, [rememberAnchor, onOpen])
    // the end marker exists only once the grid is shown
    const showsGrid = items.length > 0 && status !== 'loading' && status !== 'error'

    useEffect(() => {
        if (!HAS_OBSERVER || !sentinel.current) return undefined
        const observer = new IntersectionObserver(([entry]) => {
            nearEnd.current = entry.isIntersecting
            if (entry.isIntersecting) loadMore()
        }, { rootMargin: PRELOAD_MARGIN })
        observer.observe(sentinel.current)
        return () => observer.disconnect()
    }, [loadMore, showsGrid])

    // a short page can leave the end marker in view: keep going until it scrolls out (one request at a time)
    useEffect(() => {
        if (status === 'ready' && nearEnd.current) loadMore()
    }, [status, items.length, loadMore])

    // cards far off screen are skipped and use an estimated height; measure a real card so the estimate matches and
    // nothing shifts when they are drawn (e.g. restoring the position on Back)
    useEffect(() => {
        const card = grid.current && grid.current.querySelector('.mv-product-card')
        if (card && card.offsetHeight) grid.current.style.setProperty('--mv-feed-card-h', card.offsetHeight + 'px')
    }, [showsGrid])

    useEffect(() => {
        if (added > 0 && items.length > added) setAnnounce(`${added} more ${added === 1 ? 'style' : 'styles'} loaded`)
    }, [added, items.length])

    if (status === 'loading') return <ProductGridSkeleton count={8} />

    if (status === 'error') {
        return <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load products'
            text='Please check your connection and try again.' actionLabel='Try again' onAction={retry} />
    }

    if (items.length === 0) return empty

    return (
        <>
            <div className='mv-product-grid mv-feed-grid' ref={grid}>
                {items.map(item => <FeedCard key={item.itemId} item={item} onOpen={open} />)}
            </div>
            <div ref={sentinel} className='mv-feed-sentinel' aria-hidden='true' />

            {/* while more remain, about a screen of placeholder cards sits under the grid: a fast scroll lands on
                these instead of the footer, so the footer only moves while it is off screen */}
            {(status === 'loading-more' || (status === 'ready' && HAS_OBSERVER)) && <ProductGridSkeleton count={12} />}

            {status === 'error-more' &&
                <div className='mv-feed-status' role='alert'>
                    <span>Couldn't load more styles.</span>
                    <button type='button' className='mv-btn mv-btn-secondary' onClick={retry}>Try again</button>
                </div>}

            {status === 'ready' && !HAS_OBSERVER &&
                <div className='mv-feed-status'>
                    <button type='button' className='mv-btn mv-btn-secondary' onClick={loadMore}>Load more</button>
                </div>}

            {status === 'end' && items.length > 8 &&
                <div className='mv-feed-end'>
                    <p>You've seen all {stylesLabel(total || items.length)}</p>
                    <button type='button' className='mv-btn mv-btn-secondary' onClick={backToTop}>
                        <HiArrowUp aria-hidden='true' /> Back to top
                    </button>
                </div>}

            <p className='mv-sr-only' aria-live='polite'>{announce}</p>
        </>
    )
}

export default InfiniteProductGrid
